import { useId, useState } from 'react';
import { NavLink, Outlet, useMatch, useNavigate } from 'react-router-dom';
import { api, ErrorAlert, Icon, Spinner, useAsyncAction, type IconName } from '@pryme/ui';
import { useDev } from '../lib/context';
import { DevHeader, SignedInAs } from './Header';
import { ROLE_LABEL } from '../lib/types';

export const SERVICE_TABS: { path: string; label: string; icon: IconName }[] = [
  { path: '', label: 'Overview', icon: 'layers' },
  { path: 'credentials', label: 'Credentials', icon: 'key' },
  { path: 'redirects', label: 'Redirects & OAuth', icon: 'link' },
  { path: 'claims', label: 'Claims & consent', icon: 'shieldCheck' },
  { path: 'security', label: 'Security', icon: 'lock' },
  { path: 'sdk', label: 'SDK integration', icon: 'code' },
  { path: 'playground', label: 'API playground', icon: 'signature' },
  { path: 'mandates', label: 'Mandate playground', icon: 'agent' },
  { path: 'events', label: 'Live events', icon: 'activity' },
];

function OrgSwitcher() {
  const { orgs, org, selectOrg, reloadOrgs } = useDev();
  const id = useId();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const create = useAsyncAction();
  const navigate = useNavigate();
  return (
    <div className="dv-org">
      {orgs.length > 0 && (
        <div className="p-field">
          <label htmlFor={id}>Organization</label>
          <select
            id={id}
            className="p-select"
            value={org?.id ?? ''}
            onChange={(e) => {
              if (e.target.value === '__new') setCreating(true);
              else {
                selectOrg(e.target.value);
                navigate('/');
              }
            }}
          >
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>{o.display_name} · {ROLE_LABEL[o.role]}</option>
            ))}
            <option value="__new">Add organization…</option>
          </select>
        </div>
      )}
      {(creating || orgs.length === 0) && (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const r = await create.run(() => api<{ id: string }>('/v1/orgs', { body: { displayName: name.trim() } }));
            if (r) {
              await reloadOrgs();
              selectOrg(r.id);
              setCreating(false);
              setName('');
            }
          }}
        >
          <div className="p-field">
            <label htmlFor={`${id}-n`}>New organization name</label>
            <input id={`${id}-n`} className="p-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} required />
          </div>
          <div className="p-row" style={{ gap: '0.4rem' }}>
            <button className="p-btn small" disabled={create.busy || !name.trim()}>{create.busy ? <Spinner /> : 'Create'}</button>
            {orgs.length > 0 && <button type="button" className="p-btn ghost small" onClick={() => setCreating(false)}>Cancel</button>}
          </div>
          <ErrorAlert error={create.error} />
        </form>
      )}
    </div>
  );
}

function Item(props: { to: string; icon: IconName; label: string; end?: boolean }) {
  return (
    <NavLink to={props.to} end={props.end}>
      <Icon name={props.icon} size={16} />
      {props.label}
    </NavLink>
  );
}

export function Layout() {
  const { environment, logout, services, me } = useDev();
  const m = useMatch('/services/:serviceId/*');
  const serviceId = m?.params.serviceId && m.params.serviceId !== 'new' ? m.params.serviceId : null;
  const svc = services.find((s) => s.id === serviceId);
  return (
    <div className={`p-shell dv-shell ${environment === 'production' ? '' : 'dv-sandbox'}`}>
      <a href="#main" className="p-skip">Skip to content</a>
      <DevHeader environment={environment}>
        <SignedInAs status={me?.displayStatus} />
        <span className="p-topbar-sep p-hide-sm" aria-hidden="true" />
        <button type="button" className="p-btn secondary small" onClick={() => void logout()}>Sign out</button>
      </DevHeader>
      <div className="p-body">
        <nav className="p-nav" aria-label="Developer portal">
          <OrgSwitcher />
          <span className="p-nav-label">Overview</span>
          <Item to="/" end icon="home" label="Dashboard" />
          <Item to="/services/new" icon="plus" label="New service" />
          {serviceId && (
            <>
              <span className="p-nav-label">Service</span>
              <span className="dv-nav-service" title={svc?.displayName ?? serviceId}>{svc?.displayName ?? serviceId}</span>
              {SERVICE_TABS.map((t) => (
                <Item key={t.path} to={`/services/${serviceId}${t.path ? `/${t.path}` : ''}`} end icon={t.icon} label={t.label} />
              ))}
            </>
          )}
          <span className="p-nav-label">Organization</span>
          {environment === 'production' && <Item to="/domains" icon="globe" label="Domains" />}
          <Item to="/team" icon="users" label="Team" />
          <span className="p-nav-label">Resources</span>
          <Item to="/docs" icon="document" label="Documentation" />
        </nav>
        <main className="p-main" id="main" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
